import { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Compact cell for long free-text fields (specifications, notes).
 *
 *  • When empty: shows just a muted icon.
 *  • When filled: shows the icon + first 25 chars as a hover-able preview.
 *
 * Clicking opens a Popover with a Textarea floating over the table. Edits
 * auto-save with a 500ms debounce while typing — no need to blur/save.
 * Esc closes the popover.
 */
export const PopoverEdit = ({ value, onChange, placeholder = '', readOnly, icon: Icon, label, testId }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const saveTimerRef = useRef(null);
  const hasContent = !!(value && String(value).trim());
  const preview = hasContent ? String(value).slice(0, 25) + (String(value).length > 25 ? '…' : '') : '';

  // Sync local draft from upstream when closed (avoids state-during-render).
  useEffect(() => {
    if (!open) setVal(value ?? '');
  }, [value, open]);

  // Cancel any pending debounced save when this row unmounts.
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const openIt = (next) => {
    if (next && readOnly && !hasContent) return;
    setVal(value ?? '');
    setOpen(next);
    if (!next && saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  };

  const scheduleSave = (next) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      if (next !== (value ?? '')) onChange(next);
    }, 500);
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setVal(next);
    scheduleSave(next);
  };

  const flushAndClose = () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    if (val !== (value ?? '')) onChange(val);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={openIt}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`group inline-flex items-center gap-1 h-7 px-1.5 rounded transition-colors text-left max-w-[200px] ${hasContent
            ? 'text-emerald-800 bg-emerald-50/60 hover:bg-emerald-100'
            : 'w-7 justify-center text-[#5b6773]/35 hover:text-[#5b6773] hover:bg-[#2d948c]/5'}`}
          aria-label={label}
          title={hasContent ? value : (readOnly ? `No ${label.toLowerCase()}` : `Add ${label.toLowerCase()}`)}
          data-testid={testId}
        >
          <Icon className="h-3.5 w-3.5 flex-shrink-0" />
          {hasContent && (
            <span className="text-[11px] truncate">{preview}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-80 p-3"
        onOpenAutoFocus={(e) => { if (readOnly) e.preventDefault(); }}
        data-testid={`${testId}-popover`}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5b6773]">{label}</p>
          {!readOnly && hasContent && (
            <button
              type="button"
              onClick={() => { setVal(''); if (saveTimerRef.current) clearTimeout(saveTimerRef.current); onChange(''); setOpen(false); }}
              className="text-[10px] text-[#a8324a]/70 hover:text-[#a8324a]"
            >
              Clear
            </button>
          )}
        </div>
        {readOnly ? (
          <p className="text-xs whitespace-pre-wrap text-[#1f2933] min-h-[80px]">{value || <span className="italic text-[#9ca3af]">Empty</span>}</p>
        ) : (
          <>
            <textarea
              autoFocus
              value={val}
              onChange={handleChange}
              onBlur={flushAndClose}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { flushAndClose(); }
              }}
              placeholder={placeholder}
              rows={4}
              className="w-full text-xs rounded border border-input bg-background p-2 resize-y focus:outline-none focus:ring-2 focus:ring-ring/40"
              data-testid={`${testId}-textarea`}
            />
            <p className="text-[10px] text-[#9ca3af] mt-1">Auto-saves as you type · Esc to close</p>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
